import React, { useEffect, useState } from "react";
import { Users, Eye, TrendingUp, Globe, Smartphone, Monitor, Chrome, Rss, Clock } from "lucide-react";
import {
  AreaChart, Area, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid,
  PieChart, Pie, Cell, Legend, BarChart, Bar,
} from "recharts";
import { api } from "../../lib/api";

const COLORS = ["#A855F7", "#8B5CF6", "#5B21B6", "#C084FC", "#7C3AED", "#6D28D9"];

const StatCard = ({ label, value, sub, icon: Icon, accent }) => (
  <div data-testid={`stat-${label}`} className="rounded-xl border border-white/5 bg-[#101018] p-5 relative overflow-hidden">
    {accent && <div className="absolute -top-6 -right-6 w-24 h-24 rounded-full bg-[#A855F7]/20 blur-2xl" />}
    <div className="relative flex items-center justify-between">
      <span className="text-[10px] tracking-widest uppercase text-white/40 font-mono-ui">{label}</span>
      <Icon size={14} className={accent ? "text-[#A855F7]" : "text-white/40"} />
    </div>
    <div className="relative mt-4 font-display text-4xl text-white">{value}</div>
    {sub && <div className="relative mt-1 text-xs text-white/50">{sub}</div>}
  </div>
);

const chartTooltipStyle = {
  contentStyle: { background: "#101018", border: "1px solid rgba(168,85,247,0.4)", borderRadius: 8, color: "#fff" },
  labelStyle: { color: "#A855F7", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.15em" },
  itemStyle: { color: "#fff", fontSize: 12 },
};

export default function AdminAnalytics() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const [live, setLive] = useState(0);

  const load = () => api.get(`/admin/analytics?days=${days}`).then((r) => { setData(r.data); setLive(r.data.live_users); });

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [days]);
  // live poll every 15s
  useEffect(() => {
    const t = setInterval(() => {
      api.get(`/admin/analytics?days=${days}`).then((r) => setLive(r.data.live_users)).catch(() => {});
    }, 15000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [days]);

  if (!data) return null;

  const fmtNum = (n) => new Intl.NumberFormat("tr-TR").format(n);

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-display text-4xl uppercase tracking-tight text-white">Analiz</h1>
          <p className="text-white/50 text-sm mt-1">Site ziyaretçi ve trafik analizi</p>
        </div>
        <div className="flex items-center gap-2">
          {[7, 30, 90].map((d) => (
            <button
              key={d}
              data-testid={`range-${d}`}
              onClick={() => setDays(d)}
              className={`px-4 py-2 rounded-full text-xs uppercase tracking-widest transition-colors border ${
                days === d
                  ? "bg-[#A855F7] border-[#A855F7] text-white"
                  : "border-white/10 text-white/60 hover:text-white hover:border-white/30"
              }`}
            >
              {d} gün
            </button>
          ))}
        </div>
      </div>

      {/* Realtime + KPI cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          label="Şu An Aktif"
          value={
            <span className="flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full bg-green-400 animate-pulse" />
              {live}
            </span>
          }
          sub="son 5 dk"
          icon={Users}
          accent
        />
        <StatCard
          label="Bugün Görüntüleme"
          value={fmtNum(data.today.views)}
          sub={`${data.today.unique} tekil ziyaretçi`}
          icon={Eye}
        />
        <StatCard
          label={`${days} Gün Görüntüleme`}
          value={fmtNum(data.range.views)}
          sub={`${fmtNum(data.range.unique)} tekil ziyaretçi`}
          icon={TrendingUp}
          accent
        />
        <StatCard
          label={`${days} Gün Tekil`}
          value={fmtNum(data.range.unique)}
          sub="Session-based"
          icon={Globe}
        />
      </div>

      {/* Trend chart */}
      <div className="mt-6 rounded-xl border border-white/5 bg-[#101018] p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-white font-medium text-sm uppercase tracking-widest">Trafik Trendi</h3>
            <p className="text-white/40 text-xs mt-1">Görüntüleme vs tekil ziyaretçi</p>
          </div>
        </div>
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data.daily_series}>
              <defs>
                <linearGradient id="v" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#A855F7" stopOpacity={0.5} />
                  <stop offset="95%" stopColor="#A855F7" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="u" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#5B21B6" stopOpacity={0.5} />
                  <stop offset="95%" stopColor="#5B21B6" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
              <XAxis dataKey="day" tick={{ fill: "#666", fontSize: 10 }} tickFormatter={(d) => d.slice(5)} />
              <YAxis tick={{ fill: "#666", fontSize: 10 }} allowDecimals={false} />
              <Tooltip {...chartTooltipStyle} />
              <Area type="monotone" dataKey="views" name="Görüntüleme" stroke="#A855F7" strokeWidth={2} fill="url(#v)" />
              <Area type="monotone" dataKey="unique" name="Tekil" stroke="#5B21B6" strokeWidth={2} fill="url(#u)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Hourly bar */}
      <div className="mt-6 rounded-xl border border-white/5 bg-[#101018] p-6">
        <div className="flex items-center gap-2 mb-4">
          <Clock size={14} className="text-[#A855F7]" />
          <h3 className="text-white font-medium text-sm uppercase tracking-widest">Son 24 Saat</h3>
        </div>
        <div className="h-56">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data.hourly_series}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
              <XAxis dataKey="hour" tick={{ fill: "#666", fontSize: 10 }} tickFormatter={(h) => h.slice(11, 13) + ":00"} />
              <YAxis tick={{ fill: "#666", fontSize: 10 }} allowDecimals={false} />
              <Tooltip {...chartTooltipStyle} />
              <Bar dataKey="views" name="Görüntüleme" fill="#A855F7" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Top pages + sources */}
      <div className="mt-6 grid lg:grid-cols-2 gap-6">
        <div className="rounded-xl border border-white/5 bg-[#101018] p-6">
          <h3 className="text-white font-medium text-sm uppercase tracking-widest mb-4">En Çok Ziyaret Edilen Sayfalar</h3>
          <ul className="space-y-3">
            {data.top_pages.map((p, i) => {
              const max = Math.max(...data.top_pages.map((x) => x.count), 1);
              return (
                <li key={p.name + i}>
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span className="text-white/80 truncate max-w-[70%]">{p.name}</span>
                    <span className="text-[#A855F7] font-mono-ui text-xs">{fmtNum(p.count)}</span>
                  </div>
                  <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-[#5B21B6] to-[#A855F7]" style={{ width: `${(p.count / max) * 100}%` }} />
                  </div>
                </li>
              );
            })}
            {data.top_pages.length === 0 && <li className="text-white/40 text-sm">Henüz veri yok.</li>}
          </ul>
        </div>

        <div className="rounded-xl border border-white/5 bg-[#101018] p-6">
          <div className="flex items-center gap-2 mb-4">
            <Rss size={14} className="text-[#A855F7]" />
            <h3 className="text-white font-medium text-sm uppercase tracking-widest">Trafik Kaynakları</h3>
          </div>
          {data.top_sources.length > 0 ? (
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={data.top_sources} dataKey="count" nameKey="name" innerRadius={40} outerRadius={80} paddingAngle={2}>
                    {data.top_sources.map((_, i) => (
                      <Cell key={i} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip {...chartTooltipStyle} />
                  <Legend wrapperStyle={{ fontSize: 11, color: "#fff" }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <p className="text-white/40 text-sm">Henüz kaynak verisi yok.</p>
          )}
        </div>
      </div>

      {/* Devices + Browsers + Languages */}
      <div className="mt-6 grid md:grid-cols-3 gap-6">
        {[
          { title: "Cihazlar", icon: Smartphone, data: data.devices },
          { title: "Tarayıcılar", icon: Chrome, data: data.browsers },
          { title: "Diller", icon: Monitor, data: data.langs },
        ].map((box) => (
          <div key={box.title} className="rounded-xl border border-white/5 bg-[#101018] p-6">
            <div className="flex items-center gap-2 mb-4">
              <box.icon size={14} className="text-[#A855F7]" />
              <h3 className="text-white font-medium text-sm uppercase tracking-widest">{box.title}</h3>
            </div>
            <ul className="space-y-2">
              {box.data.map((d, i) => (
                <li key={i} className="flex items-center justify-between text-sm">
                  <span className="text-white/80">{d.name}</span>
                  <span className="text-[#A855F7] font-mono-ui text-xs">{fmtNum(d.count)}</span>
                </li>
              ))}
              {box.data.length === 0 && <li className="text-white/40 text-xs">—</li>}
            </ul>
          </div>
        ))}
      </div>

      {/* SEO tools card */}
      <div className="mt-8 rounded-xl border border-[#A855F7]/25 bg-gradient-to-br from-[#5B21B6]/15 to-[#101018] p-6">
        <h3 className="text-white font-medium text-sm uppercase tracking-widest mb-4">SEO Araçları</h3>
        <div className="grid md:grid-cols-3 gap-3">
          <a href="/api/seo/sitemap.xml" target="_blank" rel="noreferrer" className="p-4 rounded-lg border border-white/10 bg-black/30 hover:border-[#A855F7]/50 transition-colors">
            <div className="text-[10px] uppercase tracking-widest text-[#A855F7] font-mono-ui mb-1">Sitemap</div>
            <div className="text-white text-sm">sitemap.xml</div>
            <div className="text-white/50 text-xs mt-1">Google Search Console'a ekle</div>
          </a>
          <a href="/robots.txt" target="_blank" rel="noreferrer" className="p-4 rounded-lg border border-white/10 bg-black/30 hover:border-[#A855F7]/50 transition-colors">
            <div className="text-[10px] uppercase tracking-widest text-[#A855F7] font-mono-ui mb-1">Robots</div>
            <div className="text-white text-sm">robots.txt</div>
            <div className="text-white/50 text-xs mt-1">Tüm crawler'lara açık</div>
          </a>
          <a href="/llms.txt" target="_blank" rel="noreferrer" className="p-4 rounded-lg border border-white/10 bg-black/30 hover:border-[#A855F7]/50 transition-colors">
            <div className="text-[10px] uppercase tracking-widest text-[#A855F7] font-mono-ui mb-1">AI Search</div>
            <div className="text-white text-sm">llms.txt</div>
            <div className="text-white/50 text-xs mt-1">ChatGPT, Perplexity, Gemini</div>
          </a>
        </div>
      </div>
    </div>
  );
}
